import {
  createChatRequest,
  addUsersToChatRequest,
  removeUsersFromChatRequest,
  syncAdminChatsRequest,
  updateChatRequest,
  removeChatRequest,
  uploadImageRequest,
  createCompanyChatRequest,
  syncCompanyChatsRequest,
} from '../api/chat';
import {
  ChatUser,
  Company,
  COMPANY_PERMISSIONS,
  CompanyPosition,
  SelectedCompanyAppData,
  BasicSite,
  Team,
} from '../types';
import { useFirebaseAuth } from './useFirebaseAuth';
import { hasCompanyPermission } from '../utils/permissions';
import { blobToBase64 } from '../utils/blobToBase64';

const DEFAULT_COMPANY_CHAT_IMAGE_URL =
  'https://vyce-prod.s3.eu-west-1.amazonaws.com/cdn/chat-images/image.png';

const useChatRequests = (user?: ChatUser) => {
  const { firebaseAuth } = useFirebaseAuth();
  const userToAuth = {
    email: user?.email || '',
    phone: user?.phone || '',
    userId: user?.uid || '',
  };

  const syncCompanyChats = async ({
    teams = [],
    locations = [],
    companyId,
    companyName,
  }: {
    teams: Team[];
    locations: BasicSite[];
    companyId: string;
    companyName: string;
  }) => {
    if (!user) {
      console.error('User is not defined');
      return;
    }
    const token = await firebaseAuth(userToAuth);

    const transformedTeams = teams?.map(team => ({
      name: team.name,
      id: team.uuid,
      description: team.description || '',
      avatarUrl: team.logo?.url || '',
    }));
    const transformedLocations = locations?.map(location => ({
      name: location.name,
      id: location.uuid || '',
      description: location.description || '',
    }));

    if (!companyId || !token) {
      return;
    }

    try {
      await syncCompanyChatsRequest({
        companyId,
        companyName,
        creator: user,
        teams: transformedTeams,
        locations: transformedLocations,
        token: token || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const createCompanyChat = async (company: Company, creator: ChatUser) => {
    if (!company?.uuid) {
      return;
    }

    try {
      const token = await firebaseAuth(userToAuth);

      const chat = await createCompanyChatRequest({
        companyId: company?.uuid,
        entityId: company?.uuid,
        type: 'company',
        creator,
        name: company?.name || '',
        description: `This is the company chat for ${company?.name}`,
        avatarUrl: DEFAULT_COMPANY_CHAT_IMAGE_URL,
        token: token || '',
      });
      return chat;
    } catch (error) {
      console.error(error);
    }
  };

  const createLocationChat = async ({
    companyId,
    locationId,
    locationName,
    creator,
  }: {
    companyId: string;
    locationId: string;
    locationName: string;
    creator: ChatUser;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      const chat = await createCompanyChatRequest({
        companyId,
        type: 'location',
        creator,
        name: locationName,
        entityId: locationId,
        description: `This is the location chat for ${locationName}`,
        token: token || '',
      });
      return chat.data;
    } catch (error) {
      console.error(error);
    }
  };

  const createTeamChat = async ({
    companyId,
    teamId,
    teamName,
    creator,
    description,
    avatarUrl,
  }: {
    companyId: string;
    teamId: string;
    teamName: string;
    creator: ChatUser;
    description?: string;
    avatarUrl?: string;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      const chat = await createCompanyChatRequest({
        companyId,
        type: 'team',
        creator,
        name: teamName,
        entityId: teamId,
        description,
        avatarUrl,
        token: token || '',
      });
      return chat.data;
    } catch (error) {
      console.error(error);
    }
  };

  const updateChat = async ({
    companyId,
    entityId,
    name,
    description,
    avatarUrl,
    userId,
    chatId,
  }: {
    companyId: string;
    entityId?: string;
    name?: string;
    description?: string;
    avatarUrl?: string;
    userId: string;
    chatId?: string;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      const chat = await updateChatRequest({
        companyId,
        entityId,
        name,
        description,
        avatarUrl,
        token: token || '',
        userId,
        chatId,
      });
      return chat;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const syncAdminChats = async (
    companyAppData: SelectedCompanyAppData,
    position: CompanyPosition,
    chatUser: ChatUser
  ) => {
    const { teams, locations } = companyAppData || {};
    const isCompanyAdmin = hasCompanyPermission(position, COMPANY_PERMISSIONS.CAN_EDIT);
    const companyId = position?.company?.uuid;

    const entityIds = [
      ...(teams?.map(team => team.uuid) || []),
      ...(locations?.map(location => location.uuid) || []),
    ].filter(Boolean) as string[];

    const token = await firebaseAuth(userToAuth);

    if (!entityIds.length || !companyId || !token) {
      return;
    }

    if (isCompanyAdmin) {
      entityIds.push(companyId);
    }

    try {
      await syncAdminChatsRequest({
        companyId,
        entityIds,
        chatUser,
        token: token || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const removeUsersFromChat = async ({
    companyId,
    entityId,
    userIds,
    chatId,
  }: {
    companyId: string;
    entityId?: string;
    userIds: string[];
    chatId?: string;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      await removeUsersFromChatRequest({
        companyId,
        entityId,
        userIds,
        chatId,
        token: token || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const addUsersToChat = async ({
    companyId,
    entityId,
    users,
    chatId,
  }: {
    companyId: string;
    entityId?: string;
    users: ChatUser[];
    chatId?: string;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      await addUsersToChatRequest({
        companyId,
        entityId,
        chatId,
        users: users.filter(user => user.uid),
        token: token || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const removeChat = async ({
    companyId,
    entityId,
    userId,
  }: {
    companyId: string;
    entityId: string;
    userId: string;
  }) => {
    try {
      const token = await firebaseAuth(userToAuth);
      await removeChatRequest({
        companyId,
        entityId,
        userId,
        token: token || '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const uploadFileToFirebaseStorage = async (file: File) => {
    try {
      const base64File = await blobToBase64(file);
      const base64Data = base64File.split(',')[1];
      const token = await firebaseAuth(userToAuth);

      const result = await uploadImageRequest({ base64Data, token: token || '' });
      return result.data.url;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const createPrivateChat = async ({
    creator,
    chatUser,
    companyId,
    messagesRoute = '/messages',
  }: {
    creator: ChatUser;
    chatUser: ChatUser;
    companyId: string;
    messagesRoute?: string;
  }) => {
    if (creator.uid === chatUser.uid) {
      return;
    }
    try {
      const data = await createChatRequest({
        creator,
        companyId,
        profiles: [creator, chatUser],
        name: '',
        type: 'private',
      });
      if (data?.chatId) {
        return window.open(`/#${messagesRoute}?chatId=${data.chatId}&companyId=${companyId}`, '_blank');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const createGroupChat = async ({
    creator,
    participants,
    companyId,
    name,
  }: {
    creator: ChatUser;
    participants: ChatUser[];
    companyId: string;
    name: string;
  }) => {
    try {
      const data = await createChatRequest({
        creator,
        companyId,
        profiles: [creator, ...participants],
        name,
        type: 'group',
      });
      return data?.chatId;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  return {
    createCompanyChat,
    createLocationChat,
    syncAdminChats,
    createTeamChat,
    updateChat,
    removeUsersFromChat,
    addUsersToChat,
    removeChat,
    uploadFileToFirebaseStorage,
    createPrivateChat,
    createGroupChat,
    syncCompanyChats,
  };
};

export default useChatRequests;
